<template>
  <div class="faq-section">
    <h2 class="section-title">{{ $t('ebook.faq.title') }}</h2>
    <div class="faq-container">
      <div
        v-for="(item, index) in faqItems"
        :key="index"
        class="faq-item"
        :class="{ active: activeIndex === index }"
      >
        <button
          class="faq-question"
          @click="toggleQuestion(index)"
        >
          <span class="question-text">{{ item.question }}</span>
          <Icon
            name="material-symbols:expand-more"
            size="24"
            class="expand-icon"
          />
        </button>

        <Transition name="faq-slide">
          <div v-if="activeIndex === index" class="faq-answer">
            <div class="answer-content answer-text">
              <p>{{ item.answerTitle }}</p>
              <ul>
                <li v-for="pt in item.points" :key="pt">{{ pt }}</li>
              </ul>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const activeIndex = ref<number | null>(null)
const { t } = useI18n()

// FAQ数据（i18n）
const faqItems = computed(() => ([
  {
    question: t('ebook.faq.items.0.q'),
    answerTitle: t('ebook.faq.items.0.a.title'),
    points: [
      t('ebook.faq.items.0.a.points.0'),
      t('ebook.faq.items.0.a.points.1'),
      t('ebook.faq.items.0.a.points.2'),
      t('ebook.faq.items.0.a.points.3')
    ]
  },
  {
    question: t('ebook.faq.items.1.q'),
    answerTitle: t('ebook.faq.items.1.a.title'),
    points: [
      t('ebook.faq.items.1.a.points.0'),
      t('ebook.faq.items.1.a.points.1'),
      t('ebook.faq.items.1.a.points.2'),
      t('ebook.faq.items.1.a.points.3')
    ]
  },
  {
    question: t('ebook.faq.items.2.q'),
    answerTitle: t('ebook.faq.items.2.a.title'),
    points: [
      t('ebook.faq.items.2.a.points.0'),
      t('ebook.faq.items.2.a.points.1'),
      t('ebook.faq.items.2.a.points.2'),
      t('ebook.faq.items.2.a.points.3')
    ]
  },
  {
    question: t('ebook.faq.items.3.q'),
    answerTitle: t('ebook.faq.items.3.a.title'),
    points: [
      t('ebook.faq.items.3.a.points.0'),
      t('ebook.faq.items.3.a.points.1'),
      t('ebook.faq.items.3.a.points.2'),
      t('ebook.faq.items.3.a.points.3')
    ]
  },
  {
    question: t('ebook.faq.items.4.q'),
    answerTitle: t('ebook.faq.items.4.a.title'),
    points: [
      t('ebook.faq.items.4.a.points.0'),
      t('ebook.faq.items.4.a.points.1'),
      t('ebook.faq.items.4.a.points.2'),
      t('ebook.faq.items.4.a.points.3')
    ]
  },
  {
    question: t('ebook.faq.items.5.q'),
    answerTitle: t('ebook.faq.items.5.a.title'),
    points: [
      t('ebook.faq.items.5.a.points.0'),
      t('ebook.faq.items.5.a.points.1'),
      t('ebook.faq.items.5.a.points.2'),
      t('ebook.faq.items.5.a.points.3')
    ]
  },
  {
    question: t('ebook.faq.items.6.q'),
    answerTitle: t('ebook.faq.items.6.a.title'),
    points: [
      t('ebook.faq.items.6.a.points.0'),
      t('ebook.faq.items.6.a.points.1'),
      t('ebook.faq.items.6.a.points.2'),
      t('ebook.faq.items.6.a.points.3')
    ]
  }
]))

const toggleQuestion = (index: number) => {
  activeIndex.value = activeIndex.value === index ? null : index
}
</script>

<style scoped lang="scss">
@use 'sass:color';

.faq-section {
  padding: $spacing-xxl 0;
  background: $gray-50;
}

.section-title {
  text-align: center;
  font-size: 2.2rem;
  font-weight: 700;
  color: $gray-900;
  margin: 0 0 $spacing-xl 0;

  background: linear-gradient(135deg, #4A90E2 0%, #FF8C42 50%, #52C41A 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.faq-container {
  width: 100%;
  max-width: none;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: $spacing-md;
}

.faq-item {
  background: $white;
  border-radius: $border-radius-lg;
  box-shadow: 0 2px 8px rgba($black, 0.08);
  border: 1px solid $gray-200;
  transition: all 0.3s ease;
  overflow: hidden;

  &.active {
    box-shadow: 0 4px 20px rgba(#4A90E2, 0.15);
    border-color: #4A90E2;
    transform: scale(1.02);

    .faq-question {
      background: linear-gradient(135deg, rgba(#4A90E2, 0.05) 0%, rgba(#4A90E2, 0.02) 100%);
      color: #4A90E2;

      .expand-icon {
        transform: rotate(180deg);
        color: #4A90E2;
      }
    }
  }

  &:hover:not(.active) {
    box-shadow: 0 4px 12px rgba($black, 0.12);
    transform: translateY(-2px);
  }
}

.faq-question {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-lg;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(#4A90E2, 0.03);
  }

  .question-text {
    font-size: 1.1rem;
    font-weight: 600;
    color: $gray-900;
    line-height: 1.4;
    flex: 1;
    margin-right: $spacing-md;
  }

  .expand-icon {
    color: $gray-500;
    transition: all 0.3s ease;
    flex-shrink: 0;
  }
}

.faq-answer {
  border-top: 1px solid rgba(#4A90E2, 0.1);
  background: rgba(#4A90E2, 0.02);
}

.answer-content {
  padding: $spacing-lg;
}

.answer-text {
  color: $gray-700;
  line-height: 1.6;

  p {
    margin: 0 0 $spacing-md 0;
    font-size: 0.95rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    margin: $spacing-sm 0;
    padding-left: $spacing-md;

    li {
      margin-bottom: $spacing-sm;
      font-size: 0.9rem;
      line-height: 1.5;

      strong {
        color: #4A90E2;
        font-weight: 600;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

// FAQ滑动动画
.faq-slide-enter-active,
.faq-slide-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.faq-slide-enter-from,
.faq-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.faq-slide-enter-to,
.faq-slide-leave-from {
  max-height: 500px;
  opacity: 1;
}

// 响应式设计
@media (max-width: 768px) {
  .faq-section {
    padding: $spacing-xl 0;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .faq-container {
    padding: 0 $spacing-md;
    margin: 0;
  }

  .faq-question {
    padding: $spacing-md;

    .question-text {
      font-size: 1rem;
      margin-right: $spacing-sm;
    }

    .expand-icon {
      width: 20px;
      height: 20px;
    }
  }

  .answer-content {
    padding: $spacing-md;
  }

  .answer-text {
    font-size: 0.9rem;

    ul {
      padding-left: $spacing-sm;

      li {
        font-size: 0.85rem;
      }
    }
  }
}

@media (max-width: 480px) {
  .faq-item.active {
    transform: scale(1.01);
  }

  .faq-question {
    flex-direction: column;
    align-items: flex-start;
    gap: $spacing-sm;

    .question-text {
      margin-right: 0;
    }

    .expand-icon {
      align-self: flex-end;
    }
  }
}

// FAQ项目进入动画
.faq-item {
  animation: slideInFromRight 0.5s ease-out;

  &:nth-child(2) {
    animation-delay: 0.1s;
  }

  &:nth-child(3) {
    animation-delay: 0.2s;
  }

  &:nth-child(4) {
    animation-delay: 0.3s;
  }

  &:nth-child(5) {
    animation-delay: 0.4s;
  }

  &:nth-child(6) {
    animation-delay: 0.5s;
  }

  &:nth-child(7) {
    animation-delay: 0.6s;
  }
}

@keyframes slideInFromRight {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
